import type { LicenseTypeLimitAction, LoginLimitSaveBody, LoginLimitType } from './login-limit-api';

/** The part of the Login Limit form that Ask for Login ID shows, hides and saves. */
export interface LoginIdForm {
  type: LoginLimitType | '';
  /** Every type but named, which always asks. */
  askForLoginId: boolean;
  licenseTypes: ReadonlyArray<{ action: LicenseTypeLimitAction }>;
  loginIdLabel: string;
  exceptionsReport: boolean;
  reportTimeZone: string;
  /** True once either report attribute has been stored, so a later mode can clear it. */
  exceptionsStored: boolean;
}

/** A Block row turns Ask for Login ID on for any type but named, as the save body does. */
export function askOn(form: LoginIdForm): boolean {
  return form.type !== 'named'
    ? form.askForLoginId || form.licenseTypes.some((row) => row.action === 'block')
    : form.askForLoginId;
}

/** The login page asks for a Login ID, so Settings shows Login ID label. */
export function loginIdLabelShown(form: LoginIdForm): boolean {
  return form.type === 'named' || askOn(form);
}

/**
 * The gate builds the Login exceptions report only for an organization with a zone: named with
 * the report on, and any other type (concurrent, hours or none) that asks for a Login ID.
 */
export function zoneNeeded(form: LoginIdForm): boolean {
  return (form.type === 'named' && form.exceptionsReport) || (form.type !== 'named' && askOn(form));
}

/**
 * The label a save sends. A hidden label keeps its value, and one edited into an invalid value
 * and then hidden goes back to the saved one, so a field the admin cannot see never blocks Save.
 */
export function loginIdLabelToSave(form: LoginIdForm, savedLabel: string, labelValid: boolean): string {
  return loginIdLabelShown(form) || labelValid ? form.loginIdLabel.trim() : savedLabel;
}

export function exceptionsPayload(
  form: LoginIdForm,
): Pick<LoginLimitSaveBody, 'loginLimitExceptionsReport' | 'loginLimitReportTimeZone'> | Record<string, never> {
  if (form.type === 'named') {
    return {
      loginLimitExceptionsReport: form.exceptionsReport,
      loginLimitReportTimeZone: form.reportTimeZone || null,
    };
  }
  if (zoneNeeded(form)) {
    return {
      ...(form.exceptionsStored ? { loginLimitExceptionsReport: null } : {}),
      loginLimitReportTimeZone: form.reportTimeZone || null,
    };
  }
  if (!form.exceptionsStored) return {};
  // Any type but named with Ask for Login ID off keeps its zone for when Ask is on again.
  // The gate collects no Login IDs meanwhile, so no report is built.
  return {
    loginLimitExceptionsReport: null,
    loginLimitReportTimeZone: form.reportTimeZone || null,
  };
}
